import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { 
  FaSpinner, 
  FaCalendarAlt, 
  FaArrowLeft, 
  FaClock,
  FaMapMarkerAlt,
  FaUsers,
  FaShare,
  FaHeart,
  FaBookmark,
  FaExternalLinkAlt
} from "react-icons/fa";

const EventReadMore = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const fetchEvent = async () => {
    try {
      const docRef = doc(db, "events", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setEventData({ id: docSnap.id, ...docSnap.data() });
      } else {
        setEventData(null);
      }
    } catch (err) {
      console.error("Error fetching event:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventData.Heading || eventData.EventTitle,
          text: eventData.paragraph,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin h-12 w-12 text-green-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!eventData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-red-100 dark:bg-red-900 rounded-full p-4 w-16 h-16 mx-auto mb-4">
            <FaCalendarAlt className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Event Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The event you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/events')}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-colors duration-200"
          >
            <FaArrowLeft className="h-4 w-4" />
            <span>Back to Events</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative">
        {/* Background Image */}
        {eventData.image && (
          <div className="absolute inset-0 h-96">
            <img
              src={eventData.image || "/placeholder.svg"}
              alt={eventData.Heading || eventData.EventTitle}
              className={`w-full h-full object-cover transition-opacity duration-500 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.src = "/placeholder.svg?height=384&width=1200";
                setImageLoaded(true);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          </div>
        )}

        {/* Navigation */}
        <div className="relative z-10 p-4">
          <button
            onClick={() => navigate(-1)}
            className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200"
          >
            <FaArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 px-4 pb-16 pt-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <span className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                Event Details
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {eventData.Heading || eventData.EventTitle}
            </h1>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button
                onClick={() => setIsLiked(!isLiked)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  isLiked 
                    ? 'bg-red-600 text-white' 
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                }`}
              >
                <FaHeart className="h-4 w-4" />
                <span>{isLiked ? 'Liked' : 'Like'}</span>
              </button>
              
              <button
                onClick={() => setIsBookmarked(!isBookmarked)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                  isBookmarked 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                }`}
              >
                <FaBookmark className="h-4 w-4" />
                <span>{isBookmarked ? 'Saved' : 'Save'}</span>
              </button>
              
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-lg transition-all duration-200"
              >
                <FaShare className="h-4 w-4" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative -mt-16 z-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Event Meta Information */}
            <div className="p-8 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Date */}
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
                    <FaCalendarAlt className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</p>
                    <p className="text-gray-900 dark:text-white font-semibold">
                      {eventData.dateFrom 
                        ? formatDate(eventData.dateFrom) || eventData.dateFrom
                        : "Date not specified"
                      }
                    </p>
                    {eventData.dateTo && eventData.dateTo !== eventData.dateFrom && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        to {formatDate(eventData.dateTo) || eventData.dateTo}
                      </p>
                    )}
                  </div>
                </div>

                {/* Time */}
                {eventData.time && (
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                      <FaClock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Time</p>
                      <p className="text-gray-900 dark:text-white font-semibold">{eventData.time}</p>
                    </div>
                  </div>
                )}

                {/* Location */}
                {eventData.location && (
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                      <FaMapMarkerAlt className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</p>
                      <p className="text-gray-900 dark:text-white font-semibold">{eventData.location}</p>
                    </div>
                  </div>
                )}

                {/* Attendees */}
                {eventData.maxAttendees && (
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                      <FaUsers className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Capacity</p>
                      <p className="text-gray-900 dark:text-white font-semibold">
                        {eventData.currentAttendees || 0} / {eventData.maxAttendees} attendees
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Event Description */}
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">About This Event</h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  {eventData.fullDescription || eventData.paragraph || "No detailed description provided."}
                </p>
              </div>

              {/* Additional Details */}
              {eventData.additionalInfo && (
                <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Additional Information
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{eventData.additionalInfo}</p>
                </div>
              )}

              {/* Registration/Contact Info */}
              {(eventData.registrationLink || eventData.contactEmail) && (
                <div className="mt-8 p-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">
                    Registration & Contact
                  </h3>
                  <div className="space-y-3">
                    {eventData.registrationLink && (
                      <a
                        href={eventData.registrationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
                      >
                        <span>Register Now</span>
                        <FaExternalLinkAlt className="h-4 w-4" />
                      </a>
                    )}
                    {eventData.contactEmail && (
                      <p className="text-green-800 dark:text-green-200">
                        Contact: <a href={`mailto:${eventData.contactEmail}`} className="underline hover:no-underline">
                          {eventData.contactEmail}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Back to Events Button */}
          <div className="text-center py-8">
            <button
              onClick={() => navigate('/events')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-colors duration-200"
            >
              <FaArrowLeft className="h-4 w-4" />
              <span>Back to All Events</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventReadMore;